"use client";
import React, { useState } from "react";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { useAuth } from "@/contexts/AuthContext";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import FooterAd from "@/components/shared/ads/FooterAd";
import { AdRenderer } from "@/components/shared/ads/AdRenderer";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage, AdPlacement } from "@/types/advertising";
import {
  IconSearch,
  IconBellRinging,
  IconShieldCheck,
  IconVideo,
  IconChevronDown,
  IconChevronUp,
  IconUserCheck,
  IconClockCheck,
} from "@tabler/icons-react";
import { ContentSidebar } from "@/components/layouts/Others/ContentSidebar";

const PropertyRequestsPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const router = useRouter();
  const { is_logged_in, status, user, logout } = useAuth();

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // NOTE: The backend only serves ads on the homepage for now. We fetch the
  // homepage ad set here so the placements render while testing; swap back to
  // the real page once those ads are available.
  const { footerAds, headerAds, inFeedAds, popupAds, sidebarAds } = usePageAds(
    AdPage.HOME,
  );

  const steps = [
    {
      step: "01",
      title: "Set Your Preferences",
      description:
        "Tell us your target location, budget, property type (apartment, duplex, land), and must-have features in a short form.",
      icon: <IconSearch className="w-6 h-6 text-[#2baaab]" />,
    },
    {
      step: "02",
      title: "Automated & Verified Matching",
      description:
        "Our system scans thousands of listings and alerts our network of verified agents to find options matching your exact criteria.",
      icon: <IconBellRinging className="w-6 h-6 text-[#2baaab]" />,
    },
    {
      step: "03",
      title: "Use Inspection Service",
      description:
        "After finding the property that matches your preference, if you are unable to visit physically? Request our property inspection service.",
      icon: <IconVideo className="w-6 h-6 text-[#2baaab]" />,
    },
  ];

  const benefits = [
    {
      title: "No More Endless Calls",
      desc: "Stop calling dozens of agents who tell you 'that property is no longer available'. We bring active properties straight to you.",
      icon: <IconClockCheck className="w-8 h-8 text-[#2baaab]" />,
    },
    {
      title: "Verified Agents Only",
      desc: "Avoid scams and double-handling. Your request is only processed by professionals operating across Nigeria.",
      icon: <IconShieldCheck className="w-8 h-8 text-[#2baaab]" />,
    },
    {
      title: "Tailored to Your Budget",
      desc: "Specify your exact price ceiling including service charges or agreement fees so you never waste time looking at unaffordable options.",
      icon: <IconUserCheck className="w-8 h-8 text-[#2baaab]" />,
    },
    {
      title: "Access Inspection Services",
      desc: "Save transport costs and avoid Lagos/city traffic. You conserve your energy and we being you vefied property inspection report.",
      icon: <IconVideo className="w-8 h-8 text-[#2baaab]" />,
    },
  ];

  const faqs = [
    {
      q: "What is a Property Request?",
      a: "Instead of spending hours searching through outdated listings, a Property Request lets you submit your dream specifications (budget, location, features). Our system and network of verified agents will notify you as soon as matching properties are available.",
    },
    {
      q: "Does submitting a property request cost anything?",
      a: "No! Creating a property request on FindNigeriaProperties.com is 100% free. You only pay standard transaction fees if you decide to buy or rent a property.",
    },
    {
      q: "How does the Live Video Inspection work?",
      a: "If you like a matched property, you can request a Live Video Inspection. An agent or inspector will visit the property and conduct a real-time video call with you, answering your questions and showing you every corner before you make a physical trip.",
    },
    {
      q: "How soon will I get property matches?",
      a: "Depending on your location and criteria, matching options are delivered within minutes if available, or instantly when a new listing hits the market.",
    },
  ];

  return (
    <HomeWrapper
      footerSlot={footerAds[0] ? <FooterAd ad={footerAds[0]} /> : undefined}
    >
      {headerAds[0] && (
        <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
          <AdRenderer ad={headerAds[0]} placement={AdPlacement.HEADER} />
        </div>
      )}

      <ContentSidebar
        sidebarContent={
          sidebarAds?.[0] && (
            <div className="w-full mx-auto px-4 pt-4 pb-2">
              <AdRenderer ad={sidebarAds[0]} placement={AdPlacement.SIDEBAR} />
            </div>
          )
        }
        topOffset={200}
      >
        <div className={containerWidth}>
          <div className="w-full font-sans text-slate-800">
            {/* HOW IT WORKS */}
            <section
              id="how-it-works"
              className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto"
            >
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-4">
                  How Property Requests Work
                </h2>
                <p className="text-slate-600 text-base sm:text-lg">
                  We take the stress out of house hunting in 3 simple steps.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                {steps.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200/80 relative flex flex-col items-start hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between w-full mb-6">
                      <div className="p-3 bg-slate-100 rounded-xl">
                        {item.icon}
                      </div>
                      <span className="text-3xl font-black text-slate-300">
                        {item.step}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* In-feed ad (homepage set) - responsive mid-content strip */}
            {inFeedAds[0] && (
              <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-4">
                <AdRenderer ad={inFeedAds[0]} placement={AdPlacement.IN_FEED} />
              </section>
            )}

            {/* REASONS / BENEFITS */}
            <section className="bg-slate-100 py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
              <div className="max-w-7xl mx-auto">
                <div className="text-center max-w-3xl mx-auto mb-16">
                  <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-4">
                    Why Submit a Property Request?
                  </h2>
                  <p className="text-slate-600 text-base sm:text-lg">
                    Finding a home or investment property in Nigeria
                    shouldn&apos;t feel like a full-time job.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {benefits.map((b, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col"
                    >
                      <div className="mb-4">{b.icon}</div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">
                        {b.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {b.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* BANNER CTA - MATCHING HOMEPAGE DESIGN */}
            <section
              id="create-request"
              className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto"
            >
              <div className="bg-gradient-to-br from-[#0c1a24] via-[#0d222d] to-[#122b37] border border-slate-800/60 rounded-3xl p-8 sm:p-14 text-center text-white relative overflow-hidden shadow-2xl">
                {/* Soft subtle glow accent */}
                <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#2baaab]/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-[#2baaab]/5 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 max-w-3xl mx-auto">
                  <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight mb-4 text-slate-100">
                    Ready to Get Started Now?
                  </h2>
                  <p className="text-slate-300 text-base sm:text-lg mb-8 leading-relaxed max-w-2xl mx-auto">
                    Submit your first property request today and let our team
                    match you with verified listings that meet your exact
                    specifications.
                  </p>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button
                      onClick={() => {
                        const targetPath =
                          "/user/dashboard/properties/my-projects";
                        if (!is_logged_in) {
                          const redirectUri = encodeURIComponent(
                            `${window.location.origin}${targetPath}`,
                          );
                          router.push(
                            `/auth/sign-in?redirect_uri=${redirectUri}&trigger_create=true`,
                          );
                        } else {
                          router.push(targetPath);
                        }
                      }}
                      className="w-full sm:w-auto px-8 py-4 bg-[#2baaab] hover:bg-[#248d8e] text-white font-semibold rounded-xl shadow-lg hover:shadow-[#2baaab]/20 transition-all text-base cursor-pointer"
                    >
                      Submit Your Property Request
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* FREQUENTLY ASKED QUESTIONS */}
            <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  Frequently Asked Questions
                </h2>
                <p className="text-slate-600 text-sm sm:text-base">
                  Got questions about property requests and live inspections? We
                  have answers.
                </p>
              </div>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center font-semibold text-slate-900 focus:outline-none hover:bg-slate-50 transition-colors"
                    >
                      <span className="text-base sm:text-lg">{faq.q}</span>
                      {openFaq === index ? (
                        <IconChevronUp
                          size={20}
                          className="text-[#2baaab] shrink-0"
                        />
                      ) : (
                        <IconChevronDown
                          size={20}
                          className="text-slate-400 shrink-0"
                        />
                      )}
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-5 text-slate-600 text-sm sm:text-base border-t border-slate-100 pt-3 leading-relaxed">
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </ContentSidebar>
      {popupAds[0] && (
        <AdRenderer ad={popupAds[0]} placement={AdPlacement.POPUP} />
      )}
      {footerAds[0] && (
        <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
          <AdRenderer ad={footerAds[0]} placement={AdPlacement.FOOTER} />
        </div>
      )}
    </HomeWrapper>
  );
};

export default PropertyRequestsPage;
